import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibModal } from '@chewy/kib-overlays-react'

import { PaymentControls } from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/PaymentControls'
import PaymentDialogV2Title from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/components/PaymentDialogV2Title'
import InvoiceType from '~/constants/InvoiceType'

import { PaymentRequestFlowState } from '../constants/paymentRequestDialogV2Constants'
import { usePaymentRequestDialog } from '../context/PaymentRequestDialogProvider'
import { PaymentRequestScreen } from '../screens/PaymentRequestScreen'
import { PaymentRequestSentSuccessScreen } from './PaymentRequestSentSuccessScreen'

import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css'
import '@chewy/kib-transition-styles/dist/kib-transition.css'
import styles from './PaymentRequestDialogV2.module.scss'

export interface PaymentRequestDialogV2Props {
  isOpen: boolean
  onClose: () => void
  showStepper?: boolean
}

export const PaymentRequestDialogV2 = ({
  isOpen,
  onClose,
  showStepper = false,
}: PaymentRequestDialogV2Props) => {
  const { actions, dialogState, invoice } = usePaymentRequestDialog()
  const { dismissSendError, resetDialogState } = actions
  const { flowState, sendTo, successPayload } = dialogState
  const isSending = flowState === PaymentRequestFlowState.SENDING
  const isSuccess = flowState === PaymentRequestFlowState.SUCCESS
  const isSuccessView = isSuccess && successPayload != null
  const { t } = useTranslation(['Common', 'Dialogs'])
  const [isFormValid, setIsFormValid] = useState(true)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetDialogState()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, resetDialogState])

  const isDepositRequest = invoice.type === InvoiceType.ESTIMATE

  const handleModalClose = () => {
    dismissSendError()
    onClose()
  }

  let modalTitle = isDepositRequest
    ? t('Common:REQUEST_DEPOSIT_PAYMENT')
    : t('Common:REQUEST_PAYMENT')
  if (isSuccessView && successPayload != null) {
    modalTitle = successPayload.isDeposit
      ? t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SUCCESS.SENDING_DEPOSIT_TITLE')
      : t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SUCCESS.SENDING_INVOICE_TITLE')
  }

  const titleSection = (
    <PaymentDialogV2Title
      activeStep={1}
      showStepper={showStepper && !isSuccessView}
      title={modalTitle}
    />
  )

  const sendPaymentControls = () => (
    <PaymentControls
      align="left"
      disabled={!isFormValid || isSending || sendTo.length === 0}
      emphasis="primary"
      isLoading={isSending}
      primaryAction={actions.sendPayment}
      primaryText={t('Common:SEND_ACTION')}
    />
  )

  const successControls = () => (
    <PaymentControls
      align="left"
      disabled={false}
      emphasis="primary"
      isLoading={false}
      primaryAction={handleModalClose}
      primaryText={t('Common:CLOSE_ACTION')}
    />
  )

  const modalControls = isSuccessView ? successControls : sendPaymentControls

  let modalBody
  if (isSuccessView && successPayload) {
    modalBody = (
      <PaymentRequestSentSuccessScreen
        conversationId={successPayload.conversationId}
        recipientDisplay={successPayload.recipientDisplay}
      />
    )
  } else {
    modalBody = (
      <PaymentRequestScreen
        isFormValid={isFormValid}
        setIsFormValid={setIsFormValid}
      />
    )
  }

  return (
    <KibModal
      className={styles.paymentRequestDialogV2Container}
      controls={modalControls}
      open={isOpen}
      title={titleSection}
      onClose={handleModalClose}
    >
      {modalBody}
    </KibModal>
  )
}
